import React, {
  useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Spin } from 'antd';
import { DateTime } from 'luxon';
import { PropTypes } from 'prop-types';

import Permissions from '../auth/Permissions';
import Analytics from '../helpers/Analytics';

import BreadCrumbContainer from '../common/breadcrumbContainer/breadcrumbContainer';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';

import FormDetailView from '../forms/CompletedForms/FormDetailView';

import ScheduleHeader from './ScheduleHeader';
import DailySchedule from './DailySchedule/DailySchedule';
import WeeklySchedule from './WeeklySchedule/WeeklySchedule';
import MonthlySchedule from './MonthlySchedule/MonthlySchedule';
import UserSchedule from './UserSchedule/UserSchedule';
import ScheduleDrawer from './ScheduleDrawer';
import UserWeeklySchedule from './UserSchedule/UserWeeklySchedule';
import RecurringChangeModal from '../common/modals/RecurringChangeModal';

import {
  filterShifts,
  getDayKey,
  getId,
  timestampToPosition,
  prepareShiftUpdatePayload,
  refineReminders,
  getWeekStart,
  getBiWeeklyEnd,
  getMaxStartFromRestriction,
  getWeekEnd,
  getDefaultShiftTimes,
  getViewDateRange,
} from './scheduleHelpers';

import {
  getSchedule,
  createShift,
  updateShift,
  deleteShift,
  getUserFieldScheduleFilterViews,
  applyToShift,
  getScheduleCategories,
} from './state/schedule.actions';
import { getUserLabels, getUsers } from '../users/state/users.actions';
import {
  getFormById,
  getTemplates,
  getStatuses,
  getCustomTables,
} from '../forms/state/forms.actions';
import { getCustomerLabels, getCustomers } from '../contacts/customers/state/customers.actions';
import {
  getEmails,
} from '../emails/state/email.actions';
import { getBillingRates } from '../billingRates/state/billingRates.actions';

import { getIdMap, isNullOrUndefined } from '../helpers/helpers';
import { getProjects } from '../projects/state/projects.actions';
import { getDivisions } from '../settings/state/settings.actions';
import FilterDrawer from '../common/filters/FilterDrawer';
import FieldScheduleCustomFilterTab from './Filters/FieldScheduleCustomFilterTab';
import UnassignedScheduleSidebar from './UnassignedScheduleSidebar';
import { SCHEDULE_SIDEBAR_CLOSED_STORAGE_KEY, USER_SCHEDULE_VIEWS } from './schedule.constants';
import { getEquipment } from '../equipment/state/equipment.actions';
import { getMaterials } from '../materials/state/materials.actions';

import useUpdateCostcodeIdMap from '../costcodes/useUpdateCostcodeIdMap';
import exportFieldScheduleToExcel from './fieldScheduleExport';

const getViewTypeFromAsync = () => {
  const type = window.localStorage.getItem('scheduleViewType') ?? 'Week';
  if (type === 'User' || type === 'user') {
    window.localStorage.setItem('scheduleViewType', 'UserDay');
    return 'UserDay';
  }
  return type;
};
const crumbs = [{ text: 'Scheduling', icon: 'calendar' }];

const showDeleteModal = (shift, callback) => (
  CustomConfirmModal({
    title: `Delete ${shift.title}`,
    okText: 'Delete',
    cancelText: 'Cancel',
    onOk: callback,
  })
);

export default function Schedule({
  history,
}) {
  const defaultDisplay = !Permissions.has('SCHEDULE_WRITE');
  const dispatch = useDispatch();
  const stateShifts = useSelector((state) => state.schedule.shifts);
  const shiftMap = useSelector((state) => state.schedule.shiftMap);
  const filterViews = useSelector((state) => state.schedule.fieldScheduleFilterViews);
  const filters = useSelector((state) => state.schedule.fieldScheduleFilters);
  const divisions = useSelector((state) => state.settings.divisions);
  const companySettings = useSelector((state) => state.settings.company);
  const users = useSelector((state) => state.users.users);
  const projects = useSelector((state) => state.projects.projects);
  const phases = useSelector((state) => state.costcodes.phases);
  const teams = useSelector((state) => state.teams.teams);
  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);
  const formTemplates = useSelector((state) => state.forms.templates);
  const scheduleRestriction = useSelector((state) => state.settings.scheduleRestriction);

  const [viewType, setViewType] = useState(getViewTypeFromAsync());
  const [date, setDate] = useState(DateTime.local());
  const [showDrawer, setShowDrawer] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [selectedShift, setSelectedShift] = useState();
  const [newShift, setNewShift] = useState();
  const [editShift, setEditShift] = useState();
  const editShiftRef = useRef(editShift);
  editShiftRef.current = editShift;
  const [shifts, setShifts] = useState({});
  const [searchText, setSearchText] = useState();
  const [isDisplay, setIsDisplay] = useState(defaultDisplay);
  const [changeModalMode, setChangeModalMode] = useState();
  const [changeModalType, setChangeModalType] = useState();
  const [stagedUpdate, setStagedUpdate] = useState();
  const [showFormDetail, setShowFormDetail] = useState();
  const [lastDownload, setLastDownload] = useState();
  const [loading, setLoading] = useState(false);
  const [rangeStartDate, setRangeStartDate] = useState(DateTime.local());
  const [rangeEndDate, setRangeEndDate] = useState(DateTime.local());
  const [canUseHotkeys, setCanUseHotkeys] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const weeksRestriction = useMemo(() => {
    const ourUser = users.find((u) => u.id === Permissions.id);
    return scheduleRestriction[ourUser?.position]?.weeks ?? null;
  }, [users, scheduleRestriction]);

  useEffect(() => {
    const isSideBarOpen = window.localStorage.getItem(SCHEDULE_SIDEBAR_CLOSED_STORAGE_KEY) !== 'true';
    setIsSidebarOpen(isSideBarOpen);
  }, []);

  useEffect(() => {
    dispatch(getProjects());
    dispatch(getUsers());
    dispatch(getDivisions());
    dispatch(getUserFieldScheduleFilterViews());
    dispatch(getScheduleCategories());
    dispatch(getEquipment());
    dispatch(getMaterials());
  }, []);

  const hideDrawer = useCallback(() => {
    setShowDrawer(false);
    setSelectedShift();
    setNewShift();
    setEditShift();
    setIsDisplay(defaultDisplay);
    setChangeModalMode();
    setChangeModalType();
    setStagedUpdate();
  }, []);

  const openFormDetail = useCallback(async (formId) => {
    if (await dispatch(getFormById(formId))) {
      setShowFormDetail(true);
    }
  }, []);

  const closeFormDetail = useCallback(() => {
    setShowFormDetail(false);
    if (editShift) {
      if (editShift.id in shiftMap) {
        // Force reload data
        setShowDrawer(false);
        setEditShift({
          ...editShift,
          ...shiftMap[editShift.id] ?? {},
        });
        setTimeout(() => {
          setShowDrawer(true);
        }, 200);
      } else {
        hideDrawer();
      }
    }
  }, [hideDrawer, editShift, shiftMap]);

  const hideChangeModal = useCallback(() => {
    setChangeModalMode();
    setChangeModalType();
  }, []);

  const onShiftCreate = useCallback((shift) => {
    const { day } = shift;
    setSelectedShift({
      ...shift,
      date: day.toMillis(),
    });
    setShowDrawer(true);
    setIsDisplay(false);
  }, []);

  const openDrawer = useCallback(() => {
    const defaults = getDefaultShiftTimes(companySettings?.settings, { baseDay: date });
    let shiftDay = date;
    let startTime = date.set({ hour: 9, minute: 0 }).toMillis();
    let endTime = date.set({ hour: 10, minute: 0 }).toMillis();

    if (defaults) {
      const {
        day: defaultDay,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      } = defaults;

      if (defaultDay) {
        shiftDay = defaultDay;
      }
      if (defaultStartTime && defaultEndTime) {
        startTime = defaultStartTime;
        endTime = defaultEndTime;
      } else if (defaultDay) {
        startTime = shiftDay.set({ hour: 9, minute: 0 }).toMillis();
        endTime = shiftDay.set({ hour: 10, minute: 0 }).toMillis();
      }
    }

    const dayStart = shiftDay.startOf('day');
    setSelectedShift();
    setEditShift();
    setNewShift({
      id: getId(),
      day: dayStart,
      date: dayStart.toMillis(),
      startTime,
      endTime,
      top: timestampToPosition(startTime, dayStart),
      bottom: timestampToPosition(endTime, dayStart),
    });
    setShowDrawer(true);
    setIsDisplay(false);
  }, [date, companySettings]);

  const closeFilterDrawer = () => {
    setCanUseHotkeys(true);
    setShowFilterDrawer(false);
  };

  const openFilterDrawer = () => {
    setCanUseHotkeys(false);
    setShowFilterDrawer(true);
  };

  const onDateChange = useCallback((newDate) => {
    const ts = newDate.valueOf();
    const dt = DateTime.fromMillis(ts);
    setDate(dt);
  }, []);

  const dispatchShiftUpdate = useCallback(async (shift, isGroup, shiftToUpdate = null) => {
    const { emailNotification, pushNotification } = companySettings.settings;
    // Shift drop from sidebar does not set editShift
    const relevantShift = shiftToUpdate || editShift || shift;
    const day = relevantShift.day || (newShift?.day || shift.day);
    const dayKey = getDayKey(day);
    if (!(dayKey in shifts)) {
      shifts[dayKey] = [];
    }
    const {
      [dayKey]: dayShifts = [],
    } = shifts;

    const existingShift = dayShifts.find((dayShift) => dayShift.id === relevantShift.id);
    if (!existingShift) return false;
    const fullEditShift = {
      ...relevantShift,
      ...shift,
      emailNotification,
      pushNotification,
    };
    if (!shift.formTemplateId && existingShift.formTemplateId) {
      fullEditShift.formTemplateId = null;
    }
    const payload = prepareShiftUpdatePayload(existingShift, fullEditShift, isGroup);
    return dispatch(updateShift(relevantShift.id, payload));
  }, [editShift, shifts, newShift, companySettings]);

  const onShiftSave = useCallback(async (shift) => {
    const { emailNotification, pushNotification } = companySettings.settings;
    let passed = false;
    const { costcodeId } = shift;
    const splitCostcodeId = costcodeId?.split('.');
    const isSplit = splitCostcodeId?.length === 2;
    const ourCostcodeId = isSplit ? splitCostcodeId[1] : costcodeId;

    // Only update if editShift exists, or if selectedShift exists AND we're not creating
    const shiftToUpdate = editShift || (selectedShift && !newShift ? selectedShift : null);

    if (shiftToUpdate) {
      if (shiftToUpdate.groupId) {
        setChangeModalMode('update');
        setChangeModalType(shiftToUpdate.isEvent ? 'Event' : 'Shift');
        setStagedUpdate({
          ...shift,
          costcodeId: ourCostcodeId,
        });
        return;
      }
      passed = await dispatchShiftUpdate(
        { ...shift, costcodeId: ourCostcodeId },
        false,
        shiftToUpdate,
      );
    } else {
      const refinedReminders = refineReminders(shift.reminders);
      window.sessionStorage.setItem('lastReminderConfig', JSON.stringify(refinedReminders));
      const toCreate = {
        ...newShift,
        ...shift,
        reminders: refinedReminders,
        emailNotification,
        pushNotification,
        costcodeId: ourCostcodeId,
      };
      passed = await dispatch(createShift(toCreate));
    }
    if (passed) hideDrawer();
  }, [
    dispatch,
    newShift,
    editShift,
    selectedShift,
    hideDrawer,
    dispatchShiftUpdate,
    companySettings,
    shifts,
  ]);

  const onShiftApply = useCallback(async (shift, status) => {
    const userId = Permissions.id;

    dispatch(applyToShift(shift.id, { userId, status }));
  }, []);

  const onShiftSelect = useCallback((shift, setAsEdit = false) => {
    Analytics.track('Schedule/ShiftView');
    setNewShift();
    setSelectedShift(shift);
    setShowDrawer(true);
    setIsDisplay(defaultDisplay);
    if (setAsEdit) {
      setEditShift(shift);
    } else {
      setEditShift(undefined);
    }
  }, [defaultDisplay]);

  const onShiftEditAndSelect = useCallback((shift) => {
    setNewShift();
    setEditShift(shift);
    setSelectedShift(shift);
  }, []);

  const onShiftDelete = useCallback((shift) => {
    if (shift.groupId) {
      setChangeModalMode('delete');
      setChangeModalType(shift.isEvent ? 'Event' : 'Shift');
      setSelectedShift(shift);
    } else {
      showDeleteModal(shift, async () => {
        if (await dispatch(deleteShift(shift))) {
          hideDrawer();
        }
      });
    }
  }, [dispatch, hideDrawer]);

  const onShiftGroupSave = useCallback(async (mode) => {
    const isGroup = mode === 'all';
    if (changeModalMode === 'delete') {
      if (await dispatch(deleteShift(selectedShift, isGroup))) {
        hideDrawer();
      }
    } else if (await dispatchShiftUpdate(stagedUpdate, isGroup)) {
      hideDrawer();
    }
  }, [selectedShift, editShift, stagedUpdate, changeModalMode]);

  const onDaySelect = useCallback((day) => {
    Analytics.track('Schedule/ChangeView', { viewType: 'Day' });
    setDate(day);
    setViewType('Day');
    window.localStorage.setItem('scheduleViewType', 'Day');
  }, []);

  const onViewTypeChange = useCallback((newViewType) => {
    Analytics.track('Schedule/ChangeView', { viewType: newViewType });
    setViewType(newViewType);
    window.localStorage.setItem('scheduleViewType', newViewType);
  }, []);

  useEffect(() => {
    // Optimize this thing...
    if (dispatch) {
      dispatch(getUserLabels());
      dispatch(getCustomers());
      dispatch(getCustomerLabels());
      dispatch(getTemplates());
      dispatch(getEmails());
      dispatch(getBillingRates());
      dispatch(getStatuses());
      dispatch(getCustomTables());
    }
  }, [dispatch]);

  useEffect(() => {
    const currentEditShift = editShiftRef.current;
    if (!currentEditShift) return;
    const storedShift = shiftMap[currentEditShift.id];
    // Keep unsaved drag/resize times instead of reverting to the stored shift.
    const hasLocalTimeEdit = !!storedShift && (
      currentEditShift.startTime !== storedShift.startTime
      || currentEditShift.endTime !== storedShift.endTime
    );
    if (hasLocalTimeEdit) return;
    setSelectedShift(storedShift);
  }, [shiftMap]);

  useEffect(() => {
    const loadShifts = async ({ startTime, endTime }) => {
      setLoading(true);
      if (await dispatch(getSchedule({ startTime, endTime }))) {
        setLastDownload([startTime, endTime]);
      }
      setLoading(false);
    };
    if (showDrawer) return;
    let startDT = DateTime.local();
    let endDT = DateTime.local();

    switch (viewType) {
      case 'Month':
        startDT = date.startOf('month');
        endDT = date.endOf('month');
        break;
      case 'UserBiWeekly':
        startDT = getWeekStart(date);
        endDT = getBiWeeklyEnd(date);
        break;
      case 'UserWeek':
        startDT = getWeekStart(date);
        endDT = getWeekEnd(date);
        break;
      case 'UserWeekdays':
        startDT = getWeekStart(date).plus({ days: 1 });
        endDT = getWeekEnd(date).minus({ days: 1 });
        break;
      default:
        startDT = getWeekStart(date);
        endDT = startDT.plus({ days: 7 });
        break;
    }
    const startTime = startDT.toMillis();
    const endTime = endDT.toMillis();
    const hasScheduleRestriction = !isNullOrUndefined(weeksRestriction);
    if (hasScheduleRestriction && getMaxStartFromRestriction(weeksRestriction) < startTime) return;
    if (!lastDownload || lastDownload[0] > startTime || lastDownload[1] < endTime) {
      loadShifts({ startTime, endTime });
    }
  }, [dispatch, lastDownload, date, viewType, showDrawer, weeksRestriction]);

  useEffect(() => {
    setShifts(stateShifts);
  }, [stateShifts]);

  useEffect(() => {
    setViewType(getViewTypeFromAsync());
  }, []);

  useEffect(() => {
    if (viewType === 'UserBiWeekly') {
      setRangeStartDate(getWeekStart(date));
      setRangeEndDate(getBiWeeklyEnd(date));
    }

    if (viewType === 'UserWeek') {
      setRangeStartDate(getWeekStart(date));
      setRangeEndDate(getWeekEnd(date));
    }

    if (viewType === 'UserWeekdays') {
      setRangeStartDate(getWeekStart(date).plus({ days: 1 }));
      setRangeEndDate(getWeekEnd(date).minus({ days: 1 }));
    }
    // extend here for custom ranges
  }, [date, viewType]);

  const userMap = useMemo(() => getIdMap(users.filter((user) => user.active)), [users]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const phaseMap = useMemo(() => getIdMap(phases), [phases]);
  const teamMap = useMemo(() => getIdMap(teams), [teams]);
  const divisionsList = useMemo(() => Object.values(divisions), [divisions]);
  const selectedUsers = useMemo(() => {
    const {
      users: filterUsers = [],
    } = filters;
    return filterUsers;
  }, [filters]);

  const filteredShifts = useMemo(() => (
    filterShifts({
      shifts,
      day: date,
      searchText,
      userMap,
      viewType,
      rangeStartDate,
      rangeEndDate,
      filters,
      projectMap,
    })
  ), [
    searchText,
    shifts,
    userMap,
    date,
    viewType,
    rangeStartDate,
    rangeEndDate,
    filters,
    projectMap,
  ]);

  const disableHotkeys = useCallback(() => setCanUseHotkeys(false), []);
  const enableHotkeys = useCallback(() => setCanUseHotkeys(true), []);

  useEffect(() => {
    const hotkeys = (event) => {
      if (showDrawer || showFilterDrawer || !canUseHotkeys) return;
      const c = String.fromCharCode(event.keyCode);
      switch (c) {
        case 'D':
          onViewTypeChange('Day');
          break;
        case 'W':
          onViewTypeChange('Week');
          break;
        case 'M':
          onViewTypeChange('Month');
          break;
        case 'U':
          onViewTypeChange('UserDay');
          break;
        case 'B':
          onViewTypeChange('UserBiWeekly');
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', hotkeys, false);

    return () => {
      document.removeEventListener('keydown', hotkeys, false);
    };
  }, [showDrawer, canUseHotkeys, onViewTypeChange]);

  const onSidebarClose = useCallback(() => {
    window.localStorage.setItem(SCHEDULE_SIDEBAR_CLOSED_STORAGE_KEY, 'true');
    setIsSidebarOpen(false);
  }, []);

  const onShiftDrop = useCallback(async ({
    day,
    shift,
    userId,
  } = {}) => {
    const {
      startTime,
      endTime,
      users: shiftUsers = [],
    } = shift || {};

    if (!startTime || !endTime) return;

    let startTimeDT = DateTime.fromMillis(startTime);
    let endTimeDT = DateTime.fromMillis(endTime);

    const startTimeDay = startTimeDT.day;
    const endTimeDay = endTimeDT.day;
    const dtDiff = endTimeDT.diff(startTimeDT, ['days']).toObject();

    startTimeDT = startTimeDT.set({
      year: day.year,
      month: day.month,
      day: day.day,
    });

    let adjustedDay = day;

    if (startTimeDay !== endTimeDay) {
      adjustedDay = day.plus({ days: dtDiff.days });
    }

    endTimeDT = endTimeDT.set({
      year: adjustedDay.year,
      month: adjustedDay.month,
      day: adjustedDay.day,
    });

    const payload = {
      ...shift,
      startTime: startTimeDT.toMillis(),
      endTime: endTimeDT.toMillis(),
      users: [...shiftUsers, userId],
      categoryId: shift.shiftCategoryId,
    };

    await dispatchShiftUpdate(payload);
  }, [dispatchShiftUpdate]);

  const relevantShifts = useMemo(() => {
    switch (viewType) {
      case 'Day':
      case 'UserDay':
        return filteredShifts[getDayKey(date)];
      case 'UserWeekdays':
      case 'UserWeek':
      case 'Week':
      case 'Month':
      case 'UserBiWeekly':
        return filteredShifts;
      default:
        return filteredShifts;
    }
  }, [viewType, filteredShifts, date]);

  const onExport = useCallback(() => {
    const { startDate, endDate } = getViewDateRange(date, viewType);
    exportFieldScheduleToExcel({
      shifts: relevantShifts,
      startDate,
      endDate,
      userMap,
      projectMap,
      phaseMap,
      costcodeIdMap,
      divisionMap: divisions,
      teamMap,
    });
  }, [
    relevantShifts,
    date,
    viewType,
    userMap,
    projectMap,
    phaseMap,
    costcodeIdMap,
    divisions,
    teamMap,
  ]);

  const shiftArray = useMemo(() => Object.values(shifts).flat(), [shifts]);

  useUpdateCostcodeIdMap({
    items: shiftArray,
  });

  if (!Permissions.match('SCHEDULE')) {
    history.replace('/dashboard');
    return null;
  }

  return (
    <BreadCrumbContainer
      crumbs={crumbs}
      bodyStyle={{
        position: 'absolute',
        top: 71,
        left: 15,
        right: 15,
        bottom: viewType === 'User' ? 20 : 50,
        height: 'auto',
        overflowY: 'hidden',
      }}
      redesignBodyStyle={{
        marginTop: 0,
      }}
      crumbStyle={{
        margin: '10px 0px',
      }}
    >
      <Row style={{ height: '100%' }}>
        { isSidebarOpen && (
          <Col span={isSidebarOpen ? 3 : 4} style={{ height: '100%' }}>
            <UnassignedScheduleSidebar
              shifts={relevantShifts}
              onSidebarClose={onSidebarClose}
              onShiftSelect={onShiftSelect}
            />
          </Col>
        )}
        <Col span={isSidebarOpen ? 21 : 24} style={{ height: '100%' }}>
          <Row id="schedule-container" style={{ paddingBottom: viewType === 'UserBiWeekly' ? 0 : 60 }}>
            <ScheduleHeader
              viewType={viewType}
              onViewTypeChange={onViewTypeChange}
              onDateChange={onDateChange}
              date={date}
              onSearch={setSearchText}
              onAdd={openDrawer}
              onExport={onExport}
              enableHotkeys={enableHotkeys}
              disableHotkeys={disableHotkeys}
              openFilterDrawer={openFilterDrawer}
              filters={filters}
              isSideBarOpen={isSidebarOpen}
              onSideBarOpen={() => {
                setIsSidebarOpen(true);
                window.localStorage.setItem(SCHEDULE_SIDEBAR_CLOSED_STORAGE_KEY, 'false');
              }}
            />
            {viewType === 'Day' && (
            <DailySchedule
              day={date}
              onShiftCreate={onShiftCreate}
              shifts={filteredShifts[getDayKey(date)]}
              newShift={newShift}
              onNewShiftChange={setNewShift}
              onShiftSelect={onShiftSelect}
              editShift={editShift}
              onShiftEdit={onShiftEditAndSelect}
            />
            )}
            {viewType === 'Week' && (
            <WeeklySchedule
              date={date}
              onShiftCreate={onShiftCreate}
              shifts={filteredShifts}
              newShift={newShift}
              onNewShiftChange={setNewShift}
              onShiftSelect={onShiftSelect}
              editShift={editShift}
              onShiftEdit={onShiftEditAndSelect}
              onDaySelect={onDaySelect}
            />
            )}

            {viewType === 'Month'
              && (
              <MonthlySchedule
                date={date}
                onDaySelect={onDaySelect}
                shifts={filteredShifts}
                onShiftSelect={onShiftSelect}
                onShiftEdit={onShiftEditAndSelect}
              />
              )}

            {USER_SCHEDULE_VIEWS.has(viewType) && (
              <UserSchedule
                key={viewType}
                day={date}
                startDT={rangeStartDate}
                endDT={rangeEndDate}
                viewType={viewType}
                onShiftCreate={onShiftCreate}
                shifts={relevantShifts}
                newShift={newShift}
                onNewShiftChange={setNewShift}
                onShiftSelect={onShiftSelect}
                editShift={editShift}
                onShiftEdit={onShiftEditAndSelect}
                searchText={searchText}
                selectedUsers={selectedUsers}
                onShiftDrop={onShiftDrop}
              />
            )}

            {viewType === 'UserBiWeekly' && (
              <UserWeeklySchedule
                date={date}
                startDate={rangeStartDate}
                endDate={rangeEndDate}
                selectedUsers={selectedUsers}
                searchText={searchText}
                shifts={filteredShifts}
                onShiftSelect={onShiftSelect}
                onShiftEdit={onShiftEditAndSelect}
                editShift={editShift}
                onShiftCreate={onShiftCreate}
                newShift={newShift}
                onNewShiftChange={setNewShift}
                onDaySelect={onDaySelect}
                viewType={viewType}
              />
            )}
            {
              loading
              && (
              <Row className="schedule-loading-container" justify="center" align="middle">
                <Col>
                  <Spin />
                </Col>
              </Row>
              )
            }
          </Row>
        </Col>
      </Row>
      <ScheduleDrawer
        shift={selectedShift || editShift || newShift}
        visible={showDrawer}
        onClose={hideDrawer}
        onSave={onShiftSave}
        onDelete={onShiftDelete}
        isNew={newShift}
        isDisplay={isDisplay}
        openFormDetail={openFormDetail}
        shifts={shifts}
        shiftMap={shiftMap}
        lastDownload={lastDownload}
        setLastDownload={setLastDownload}
        onApply={onShiftApply}
      />
      <RecurringChangeModal
        mode={changeModalMode}
        onClose={hideChangeModal}
        onSave={onShiftGroupSave}
        type={changeModalType}
      />
      <FormDetailView
        visible={showFormDetail}
        onClose={closeFormDetail}
        userMap={userMap}
        shouldRedirect={false}
      />
      <FilterDrawer
        visible={showFilterDrawer}
        onClose={closeFilterDrawer}
        filterViews={filterViews}
        CustomFilterTab={FieldScheduleCustomFilterTab}
        users={users}
        projects={projects}
        phases={phases}
        divisions={divisionsList}
        teams={teams}
        formTemplates={formTemplates}
      />
    </BreadCrumbContainer>
  );
}

Schedule.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
};

Schedule.defaultProps = {
  history: {},
};
